import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Home, FileText, MessageSquare, TrendingUp, Eye } from "lucide-react";

export const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);
  
  const statCards = [
    {
      title: "Biens actifs",
      value: stats?.active_properties || 0,
      subtitle: `${stats?.sale_properties || 0} ventes • ${stats?.rental_properties || 0} locations`,
      icon: Home,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      link: "/admin/biens"
    },
    {
      title: "Documents en attente",
      value: stats?.pending_documents || 0,
      subtitle: "À examiner",
      icon: FileText,
      color: "text-orange-600",
      bgColor: "bg-orange-100",
      link: "/admin/documents"
    },
    {
      title: "Nouveaux messages",
      value: stats?.new_contacts || 0,
      subtitle: `${stats?.total_contacts || 0} messages au total`,
      icon: MessageSquare,
      color: "text-green-600",
      bgColor: "bg-green-100",
      link: "/admin/contacts"
    },
    {
      title: "Total des biens",
      value: stats?.total_properties || 0,
      subtitle: "Dans votre catalogue",
      icon: TrendingUp,
      color: "text-[#D4AF37]",
      bgColor: "bg-[#D4AF37]/10",
      link: "/admin/biens"
    }
  ];
  
  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl text-[#0A0A0A]">Tableau de bord</h1>
          <p className="text-gray-500 mt-1">Bienvenue dans votre espace d'administration</p>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <Link key={index} to={stat.link}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-0 shadow-sm" data-testid={`stat-card-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                      <p className="text-3xl font-serif font-semibold text-[#0A0A0A]">
                        {loading ? "..." : stat.value}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">{stat.subtitle}</p>
                    </div>
                    <div className={`w-12 h-12 ${stat.bgColor} rounded-full flex items-center justify-center`}>
                      <stat.icon className={stat.color} size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        
        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-medium">Actions rapides</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link 
                to="/admin/biens" 
                className="flex items-center gap-3 p-4 rounded-sm bg-[#F9F9F9] hover:bg-[#D4AF37]/10 transition-colors"
                data-testid="quick-action-add-property"
              >
                <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                  <Home className="text-[#D4AF37]" size={20} />
                </div>
                <div>
                  <p className="font-medium text-[#0A0A0A]">Ajouter un bien</p>
                  <p className="text-sm text-gray-500">Publier une nouvelle annonce</p>
                </div>
              </Link>
              
              <Link 
                to="/admin/documents" 
                className="flex items-center gap-3 p-4 rounded-sm bg-[#F9F9F9] hover:bg-[#D4AF37]/10 transition-colors"
                data-testid="quick-action-view-documents"
              >
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <FileText className="text-orange-600" size={20} />
                </div>
                <div>
                  <p className="font-medium text-[#0A0A0A]">Voir les documents</p>
                  <p className="text-sm text-gray-500">
                    {stats?.pending_documents || 0} document(s) en attente
                  </p>
                </div>
              </Link>
              
              <Link 
                to="/admin/contacts" 
                className="flex items-center gap-3 p-4 rounded-sm bg-[#F9F9F9] hover:bg-[#D4AF37]/10 transition-colors"
                data-testid="quick-action-view-messages"
              >
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <MessageSquare className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="font-medium text-[#0A0A0A]">Voir les messages</p>
                  <p className="text-sm text-gray-500">
                    {stats?.new_contacts || 0} nouveau(x) message(s)
                  </p>
                </div>
              </Link>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-medium">Aperçu du site</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="aspect-video bg-[#0A0A0A] rounded-sm overflow-hidden relative group">
                <img 
                  src="/hero-loroux.jpg" 
                  alt="Aperçu du site"
                  className="w-full h-full object-cover opacity-50"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Link 
                    to="/" 
                    target="_blank"
                    className="flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 rounded-sm font-medium hover:bg-[#AA771C] transition-colors"
                    data-testid="view-site-button"
                  >
                    <Eye size={18} />
                    Voir le site
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
